import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { color, ColorKeys, Theme, TypographyKeys } from '../theme';
import { mediaDesktop } from '../theme/breakpoints';

export interface Props {
  variant?: TypographyKeys;
  bold?: boolean;
  visuallyHidden?: boolean;
  as?: string;
  color?: ColorKeys;
  block?: boolean;
  sizeOverride?: string;
}

export const getTextVariant =
  (
    variant: TypographyKeys = 'text5',
    bold = false,
    block = false,
    sizeOverride?: string,
  ) =>
  ({ theme }: { theme: Theme }) => {
    const font = theme.typography[variant];
    const family =
      bold && font.family === '"SofiaProSoft", sans-serif'
        ? '"SofiaProSoftBold", sans-serif'
        : font.family;

    return css`
      font-family: ${family};
      font-weight: ${font.weight};
      letter-spacing: ${font.spacing || 'normal'};
      text-transform: ${font.transform || 'none'};

      font-size: ${sizeOverride ?? font.mobile.size};
      line-height: ${font.mobile.leader};

      ${mediaDesktop} {
        font-size: ${sizeOverride ?? font.desktop.size};
        line-height: ${font.desktop.leader};
      }

      display: ${variant.includes('heading') || block ? 'block' : 'inline'};
    `;
  };

export const Text = styled.span<Props>(
  ({
    theme,
    variant = 'text4',
    bold = false,
    visuallyHidden = false,
    color: colorKey = 'greyDark' as ColorKeys,
    block,
    sizeOverride,
  }) => {
    if (visuallyHidden) {
      return css`
        border: 0;
        clip: rect(1px, 1px, 1px, 1px);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;
        word-wrap: normal !important;
      `;
    }
    const textStyles = getTextVariant(
      variant,
      bold,
      block,
      sizeOverride,
    )({ theme });

    if (colorKey) {
      textStyles.styles += `;color: ${color(colorKey as ColorKeys)({
        theme,
      })};`;
    }
    return textStyles;
  },
);

export const BlockText = styled(Text)`
  display: inline-block;
`;

export const H1 = styled(Text)``;

H1.defaultProps = {
  variant: 'heading1',
  color: 'greyDark',
  as: 'h1',
};

export const H2 = styled(Text)``;

H2.defaultProps = {
  variant: 'heading2',
  color: 'greyDark',
  as: 'h2',
};

export const H3 = styled(Text)``;

H3.defaultProps = {
  variant: 'heading3',
  color: 'greyDark',
  as: 'h3',
};

export const H4 = styled(Text)``;

H4.defaultProps = {
  variant: 'heading4',
  color: 'greyDark',
  as: 'h4',
};

export const H5 = styled(Text)``;

H5.defaultProps = {
  variant: 'heading5',
  color: 'greyDark',
  as: 'h5',
};

export const H6 = styled(Text)``;

H6.defaultProps = {
  variant: 'heading6',
  color: 'greyDark',
  as: 'h6',
};
